import React, { useMemo, useState } from 'react';
import styles from './ReviewFooter.module.scss';
import { Button } from '../../../components';
import { reviewStatus } from '../../../constants/misc';
import { useReviewFooterHelpers } from './utils';
import ReviewInput from './ReviewInput';
import Review from './Review';
import { loader } from 'graphql.macro';
import { useMutation } from '@apollo/client';
import { Text } from '../../../components/typography';
import { useOnlineStatus } from '../../../hooks/offline-hooks/offline-misc';
import { useCurrentUser } from '../../../providers/UserProvider';
import useCheckPermission from '../../../providers/UsePermissionHook';
import { actions } from '../../../utilities';
import { useIsMobile } from '../../../hooks/misc';
import { keys } from '../../../utilities/translator/translation_keys';
import { useTranslation } from 'react-i18next';

const updateReviewerMutation = loader('./ReviewFooter.update.graphql');
const updateInspectionMutation = loader(
  './ReviewFooter.readyForReview.graphql',
);
const addReviewMutation = loader('./ReviewFooter.add.graphql');

export default function ReviewFooter({
  allQuestionsAnswered,
  inspection,
  currentUser,
  onComplete,
  onSecondSight,
  publishInspection,
  refetch = () => {},
}) {
  const { t } = useTranslation();
  const { isAdmin, assessmentName } = useCurrentUser();
  const isOnline = useOnlineStatus();
  const isMobile = useIsMobile();
  const [updateReviewer] = useMutation(updateReviewerMutation);
  const [updateInspection] = useMutation(updateInspectionMutation);
  const [addReview] = useMutation(addReviewMutation);

  const [editingReview, setEditingReview] = useState(false);
  const { reviewers, participants, inspectionType } = inspection;
  const { isApproved, isParticipant, isReviewer, hasChangeRequest } =
    useReviewFooterHelpers({
      reviewers,
      currentUser,
      participants,
      inspectionType,
    });

  const myReview = useMemo(() => {
    return reviewers.find((r) => r.user.id === currentUser.id);
  }, [currentUser.id, reviewers]);

  const showReadyForReviewButton = useMemo(() => {
    if (
      isOnline &&
      inspection.template.reviewRequired &&
      inspection.isDraft &&
      !inspection.readyForReview
    ) {
      return true;
    }
    return false;
  }, [
    inspection.isDraft,
    inspection.readyForReview,
    inspection.template.reviewRequired,
    isOnline,
  ]);

  const usageCheckValid = useMemo(() => {
    const { vehicle } = inspection;
    if (!vehicle || !vehicle?.usageUpdateRequired) {
      return true;
    } else if (vehicle?.usageUpdateRequired) {
      let valid = true;

      if (vehicle.showDistance && !inspection.odometer) {
        valid = false;
      }

      if (vehicle.showHours && !inspection.hours) {
        valid = false;
      }
      return valid;
    } else {
      return false;
    }
  }, [inspection]);

  const showPublishButton = useMemo(() => {
    if (
      isOnline &&
      inspection.template.reviewRequired &&
      isParticipant &&
      inspection.readyForReview &&
      inspection.isDraft
    ) {
      return true;
    }
    return false;
  }, [
    inspection.isDraft,
    inspection.readyForReview,
    inspection.template.reviewRequired,
    isOnline,
    isParticipant,
  ]);

  const showOriginalCompleteButton = useMemo(() => {
    if (
      !inspection.template.reviewRequired &&
      inspection.isDraft &&
      (isParticipant || isAdmin)
    ) {
      return true;
    }
    return false;
  }, [
    inspection.isDraft,
    inspection.template.reviewRequired,
    isAdmin,
    isParticipant,
  ]);

  const showOfflineMessage = useMemo(() => {
    return !isOnline && inspection.template.reviewRequired;
  }, [inspection.template.reviewRequired, isOnline]);

  const hasPermission =
    useCheckPermission({
      action: actions.review,
      model: 'inspection',
      type: inspection?.inspectionType,
    }) || isReviewer;

  const showReviewInput = useMemo(() => {
    // If I'm a reviewer, the assessment is ready for a review and not published
    if (
      inspection.template.reviewRequired &&
      (isReviewer || hasPermission) &&
      isOnline &&
      inspection.readyForReview &&
      inspection.isDraft
    ) {
      // If I haven't submitted a review, or I'm editing my review
      if (
        myReview?.status === reviewStatus.open ||
        editingReview ||
        !myReview
      ) {
        return true;
      }
    }
    return false;
  }, [
    editingReview,
    inspection.isDraft,
    inspection.readyForReview,
    inspection.template.reviewRequired,
    isOnline,
    isReviewer,
    myReview,
    hasPermission,
  ]);

  return (
    <div className={styles.container}>
      <div className={styles.reviewsContainer}>
        {inspection.reviewers?.map((r) => (
          <Review
            key={`Review:${r.id}`}
            isEditing={editingReview}
            editable={r.user.id === currentUser.id && inspection.isDraft}
            deletable={isAdmin && inspection.isDraft}
            reviewer={r}
            onEditClick={() => setEditingReview(true)}
            onCancelEdit={() => setEditingReview(false)}
            onDelete={() => {
              updateReviewer({
                variables: {
                  reviewerId: r.id,
                  status: reviewStatus.open,
                  notes: '',
                },
              });
            }}
          />
        ))}
      </div>

      {showReadyForReviewButton && (
        <div className={styles.startReviewButtons}>
          <Text
            size={isMobile ? 'xs' : 'sm'}
            color="secondary"
            weight="semiBold"
            noMargin
          >
            {isParticipant || isAdmin
              ? allQuestionsAnswered
                ? !usageCheckValid
                  ? t(keys.review_footer.USAGE)
                  : inspection.template.reviewRequired && reviewers.length === 0
                  ? t(keys.review_footer.NO_REVIEWERS)
                  : t(keys.review_footer.READY)
                : t(keys.review_footer.REQUIRED_QUESTION)
              : t(keys.review_footer.PARTICIPANT)}
          </Text>
          <Button
            className={styles.readyForReviewButton}
            size="md"
            value={t(keys.review_footer.TITLE)}
            disabled={
              !allQuestionsAnswered ||
              (inspection.template.reviewRequired && reviewers.length === 0) ||
              !isParticipant ||
              !usageCheckValid
            }
            onClick={() => {
              if (inspection.template.analysisRequested) {
                onSecondSight(() => {
                  updateInspection({
                    variables: {
                      assessmentId: inspection.id,
                      readyForReview: true,
                      isDraft: true,
                    },
                  }).then(refetch);
                });
              } else {
                updateInspection({
                  variables: {
                    assessmentId: inspection.id,
                    readyForReview: true,
                    isDraft: true,
                  },
                }).then(refetch);
              }
            }}
          />
        </div>
      )}
      {showReviewInput ? (
        myReview ? (
          <ReviewInput
            isEditing={editingReview}
            onSubmit={({ status, notes }) => {
              updateReviewer({
                variables: {
                  reviewerId: myReview.id,
                  status,
                  notes,
                },
              })
                .then((res) => {
                  setEditingReview(false);
                  const responseStatus =
                    res.data?.updateInspectionReviewer?.status;
                  if (responseStatus === reviewStatus.approved) {
                    publishInspection();
                  }
                })
                .finally(refetch);
            }}
            disabled={!hasPermission}
          />
        ) : (
          <Button
            value={t(keys.review_footer.ADD)}
            align={'right'}
            onClick={() =>
              addReview({
                variables: {
                  inspectionId: inspection?.id,
                  userId: currentUser?.id,
                },
              }).then(refetch)
            }
          />
        )
      ) : (
        showPublishButton && (
          <div className={styles.startReviewButtons}>
            <Text
              size={isMobile ? 'xs' : 'sm'}
              color="secondary"
              weight="semiBold"
              noMargin
            >
              {isApproved || hasChangeRequest
                ? hasChangeRequest
                  ? t(keys.review_footer.CHANGE_REQUEST)
                  : t(keys.review_footer.COMPLETE_REVIEWS)
                : t(keys.review_footer.NEEDS_APPROVAL)}
            </Text>
            <Button
              className={styles.readyForReviewButton}
              icon={hasChangeRequest && 'edit'}
              size="md"
              value={
                hasChangeRequest ? t(keys.action.EDIT) : t(keys.common.COMPLETE)
              }
              disabled={!hasChangeRequest && !isApproved}
              onClick={() => {
                if (hasChangeRequest) {
                  updateInspection({
                    variables: {
                      assessmentId: inspection.id,
                      readyForReview: false,
                      isDraft: true,
                    },
                  });
                } else {
                  onComplete();
                }
              }}
            />
          </div>
        )
      )}

      {showOfflineMessage && (
        <Text size={isMobile ? 'xs' : 'sm'} color="secondary" weight="semiBold">
          {t(keys.review_footer.OFFLINE)}
        </Text>
      )}

      {showOriginalCompleteButton && (
        <div className={styles.startReviewButtons}>
          {!isMobile && (
            <Text
              size={isMobile ? 'xs' : 'sm'}
              color="secondary"
              weight="semiBold"
              noMargin
              textAlign="center"
            >
              {allQuestionsAnswered
                ? !usageCheckValid
                  ? t(keys.review_footer.USAGE)
                  : t(keys.review_footer.COMPLETE, {
                      variable:
                        inspection.inspectionType === 'AUDIT'
                          ? 'audit'
                          : assessmentName || t(keys.common.ASSESSMENT),
                    })
                : t(keys.review_footer.REQUIRED_QUESTIONS_NO_REVIEW)}
            </Text>
          )}
          <Button
            className={styles.readyForReviewButton}
            value={t(keys.common.COMPLETE)}
            disabled={!allQuestionsAnswered || !usageCheckValid}
            onClick={onComplete}
          />
        </div>
      )}
    </div>
  );
}
